import {
  Component,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  HostListener,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSliderChange } from '@angular/material/slider';
import { Subscription, catchError, throwError } from 'rxjs';
import { RemoteAccessClient } from 'src/app/shared/clients/remote-access.client';
import { ConfigCommand } from 'src/app/shared/models/commands/ConfigCommand';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { ToastService } from 'src/app/shared/services/toast.service';

const DEFAULT_MIN_REFRESH_RATE_SECONDS = 1.0;
const DIAGNOSTIC_MIN_REFRESH_RATE_SECONDS = 0.2;
const DEFAULT_QUALITY_PERCENTAGE= 50;
const DEFAULT_REFRESH_RATE_MILLISECONDS= 1000;
const DEFAULT_COMMAND_POLL_MILLISECONDS= 100;

@Component({
  selector: 'settings-menu-button',
  templateUrl: './settings-menu-button.component.html',
  styleUrls: ['./settings-menu-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SettingsMenuButtonComponent implements OnDestroy {
  @Input()
  ccuId: string;

  @Input()
  sessionId: string;

  @ViewChild('settingsMenuTrigger') settingsMenuTrigger: MatMenuTrigger;

  menuOpenSubscription: Subscription;

  qualityPercentage: number = DEFAULT_QUALITY_PERCENTAGE;
  refreshIntervalMillis: number = DEFAULT_REFRESH_RATE_MILLISECONDS;
  commandPollMillis: number = DEFAULT_COMMAND_POLL_MILLISECONDS;
  minRefreshRate = DEFAULT_MIN_REFRESH_RATE_SECONDS;
  staleSessionTimeoutSeconds: number;
  shiftPressed = false;

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.shiftKey) {
      this.shiftPressed = true;
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (!event.shiftKey) {
      this.shiftPressed = false;
    }
  }

  get isChanged(): boolean {
    return this.qualityPercentage !== this.configurationService.remoteAccessConfig.quality
      || this.refreshIntervalMillis !== this.configurationService.remoteAccessConfig.refreshFrequency
      || this.commandPollMillis !== this.configurationService.remoteAccessConfig.commandPollMillis
      || this.staleSessionTimeoutSeconds !== this.configurationService.getStaleSessionThresholdSeconds()
      ;  
  }

  constructor(
    private configurationService: ConfigurationService,
    private remoteAccessClient: RemoteAccessClient,
    private toastService: ToastService,
  ) {
    this.staleSessionTimeoutSeconds = this.configurationService.getStaleSessionThresholdSeconds();
  }

  ngAfterViewInit(): void {
    this.menuOpenSubscription = this.subscribeToMenuOpen();
  }

  /**
   * Subscribes to the menuOpened event of the settings menu trigger and
   * refreshes the settings to the currently applied values when the menu is opened.
   *
   * @returns Subscription
   */
  private subscribeToMenuOpen(): Subscription {
    return this.settingsMenuTrigger.menuOpened.subscribe(() => {
      this.applyConfigDefaults();
    });
  }

  private applyConfigDefaults(): void {
    this.qualityPercentage =
      this.configurationService.remoteAccessConfig.quality;

    this.refreshIntervalMillis =
      this.configurationService.remoteAccessConfig.refreshFrequency;

    this.commandPollMillis =
      this.configurationService.remoteAccessConfig.commandPollMillis;

    this.staleSessionTimeoutSeconds = this.configurationService.getStaleSessionThresholdSeconds(); 

    // Allow minimum refresh rate to be unlocked for those in the know
    if ( this.shiftPressed ) {
      this.minRefreshRate = DIAGNOSTIC_MIN_REFRESH_RATE_SECONDS;
    } else {
      this.minRefreshRate = DEFAULT_MIN_REFRESH_RATE_SECONDS;
      if (this.refreshIntervalMillis < this.minRefreshRate * 1000) {
        this.refreshIntervalMillis = DEFAULT_REFRESH_RATE_MILLISECONDS;
      
     }
    }
  }

  handleQualityChange(change: MatSliderChange): void {
    this.qualityPercentage = change.value;
  }

  handleRefreshIntervalChange(change: MatSliderChange): void {
    this.refreshIntervalMillis = change.value * 1000;
  }

  handlePollIntervalChange(change: MatSliderChange): void {
    this.commandPollMillis = change.value * 1000;
  }

  handleStaleSessionTimeoutChange(change: MatSliderChange): void {
    this.staleSessionTimeoutSeconds = change.value;
  }

  handleCancel(): void {
    this.settingsMenuTrigger.closeMenu();
  }

  handleApply(): void {
    const configCommand = new ConfigCommand(
      this.refreshIntervalMillis,
      this.qualityPercentage,
      this.configurationService.remoteAccessConfig.imageType,
      this.commandPollMillis,
    );

    // 'isChanged' also controls whether the apply button is enabled or not.
    // So this really shouldn't happen, but just in case.
    if (!this.isChanged) {
        this.toastService.success('Nothing Changed');
        return;
    }

    // Update the stale session threshold in the configuration service
    this.configurationService.setStaleSessionThresholdSeconds(this.staleSessionTimeoutSeconds);

    this.remoteAccessClient
      .postCommand(
        this.sessionId,
        this.ccuId,
        configCommand.toCommandString(),
        new Date().getTime(),
      )
      .pipe(
        catchError((error) => {
          this.toastService.error('Failed to apply settings change');
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        // Results in an event being broadcast to the `configChangeSubject` in the ConfigurationService.
        // This is then picked up by the Screenshot component which then refreshes its screenshot poll loop with the new settings.
        this.configurationService.updateSessionSettings({
          qualityPercentage: this.qualityPercentage,
          refreshIntervalMillis: this.refreshIntervalMillis,
          commandPollMillis: this.commandPollMillis,
        });

        this.toastService.success('Settings Applied');

        this.settingsMenuTrigger.closeMenu();
      });
  }

  ngOnDestroy(): void {
    this.menuOpenSubscription?.unsubscribe();
  }
}
